import { cognitoAuth } from './CognitoAuth';
import { isRetryableCognitoError } from './CognitoErrors';
import { dataResidencyManager, SupportedDataResidency } from './DataResidencyManager';
import { initializeAmplifyForDataResidency } from './DataResidencyAwareAuthHelpers';
import { refreshEndpoints } from './BackendInterface';

// Sign-in establishes access to an as-yet-unknown data residency. Rather than retrying a single
// residency deeply, we try every residency once per round; if a round saw only transient (retryable)
// errors we back off and cycle the whole set again, up to SIGN_IN_MAX_ROUNDS rounds.
export const SIGN_IN_MAX_ROUNDS = 5;
export const SIGN_IN_ROUND_BASE_DELAY_MS = 1000;
export const SIGN_IN_ROUND_MAX_DELAY_MS = 8000;
const SIGN_IN_ROUND_JITTER_MAX_MS = 250;

type SleepFunction = (delayMs: number) => Promise<void>;
type JitterFunction = () => number;

export interface SignInRetryStatus {
  attempt: number;
  maxAttempts: number;
}

export interface IntelligentSignInOptions {
  onRetry?: (status: SignInRetryStatus) => void;
  sleep?: SleepFunction;
  getJitterMs?: JitterFunction;
}

export function getSignInRoundDelayMs(roundIndex: number, jitterMs = 0): number {
  const exponentialDelay = SIGN_IN_ROUND_BASE_DELAY_MS * 2 ** roundIndex;
  return Math.min(exponentialDelay + jitterMs, SIGN_IN_ROUND_MAX_DELAY_MS);
}

function getDefaultSignInJitterMs(): number {
  return Math.floor(Math.random() * SIGN_IN_ROUND_JITTER_MAX_MS);
}

function defaultSignInSleep(delayMs: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, delayMs);
  });
}

interface LoginClues {
  ipDataResidency: SupportedDataResidency;
  storedDataResidency?: SupportedDataResidency;
  cachedDataResidency?: SupportedDataResidency;
  emailDomain?: string;
  browserLanguage?: string;
  timeZone?: string;
}

interface UserDataResidencyCache {
  email: string;
  dataResidency: SupportedDataResidency;
  lastSuccess: Date;
  confidence: 'HIGH' | 'MEDIUM' | 'LOW';
}

interface AuthenticationResult {
  user: any;
  dataResidency: SupportedDataResidency;
  attemptCount: number;
  cluesUsed: LoginClues;
}

class DataResidencyCache {
  private static CACHE_KEY = 'peekapak.userDataResidencyCache';
  private static CACHE_DURATION = 30 * 24 * 60 * 60 * 1000; // 30 days

  static getCache(): UserDataResidencyCache[] {
    try {
      const cached = localStorage.getItem(this.CACHE_KEY);
      return cached ? JSON.parse(cached) : [];
    } catch (error) {
      console.warn('Failed to load data residency cache:', error);
      return [];
    }
  }

  static cacheSuccessfulLogin(email: string, dataResidency: SupportedDataResidency): void {
    try {
      const cache = this.getCache().filter((c) => c.email !== email);

      cache.push({
        email,
        dataResidency,
        lastSuccess: new Date(),
        confidence: 'HIGH',
      });

      // Keep only recent entries (last 30 days) and limit to 50 entries
      const recent = cache
        .filter((c) => Date.now() - new Date(c.lastSuccess).getTime() < this.CACHE_DURATION)
        .slice(-50);

      localStorage.setItem(this.CACHE_KEY, JSON.stringify(recent));
    } catch (error) {
      console.warn('Failed to cache successful login:', error);
    }
  }

  static getCachedDataResidency(email: string): SupportedDataResidency | null {
    try {
      const cache = this.getCache();
      const userCache = cache.find((c) => c.email === email);

      if (userCache && Date.now() - new Date(userCache.lastSuccess).getTime() < this.CACHE_DURATION) {
        return userCache.dataResidency;
      }

      return null;
    } catch (error) {
      console.warn('Failed to get cached data residency:', error);
      return null;
    }
  }

  static clearCache(): void {
    try {
      localStorage.removeItem(this.CACHE_KEY);
    } catch (error) {
      console.warn('Failed to clear data residency cache:', error);
    }
  }
}

/**
 * Analyze multiple signals to predict the most likely data residency for a user
 */
async function predictUserDataResidency(email: string): Promise<LoginClues> {
  const clues: LoginClues = {
    ipDataResidency: SupportedDataResidency.USA, // Default fallback
  };

  // 1. Check cached successful data residency (strongest indicator)
  const cachedDataResidency = DataResidencyCache.getCachedDataResidency(email);
  if (cachedDataResidency) {
    clues.cachedDataResidency = cachedDataResidency;
  }

  // 2. Check localStorage stored data residency
  const storedDataResidency = localStorage.getItem('peekapak.userDataResidency') as SupportedDataResidency;
  if (storedDataResidency && Object.values(SupportedDataResidency).includes(storedDataResidency)) {
    clues.storedDataResidency = storedDataResidency;
  }

  // 3. Get IP-based data residency detection
  try {
    clues.ipDataResidency = await dataResidencyManager.detectUserDataResidencyFromIP();
  } catch (error) {
    console.warn('IP data residency detection failed, using US default:', error);
    clues.ipDataResidency = SupportedDataResidency.USA;
  }

  // 4. Analyze email domain for ARE (UAE/Qatar) indicators
  const emailLower = email.toLowerCase();
  if (
    emailLower.endsWith('.ae') ||
    emailLower.includes('dubai') ||
    emailLower.includes('abudhabi') ||
    emailLower.includes('sharjah') ||
    emailLower.includes('ajman') ||
    emailLower.includes('emirates')
  ) {
    clues.emailDomain = 'are-indicator';
  }

  // 5. Check browser language preference
  try {
    const lang = window.navigator.language || (window.navigator.languages && window.navigator.languages[0]);
    if (lang === 'ar-AE' || lang === 'en-AE') {
      clues.browserLanguage = 'are-indicator';
    }
  } catch (_error) {
    // Browser doesn't support navigator.language
  }

  // 6. Check timezone
  try {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (
      timeZone === 'Asia/Dubai' ||
      timeZone === 'Asia/Muscat' ||
      timeZone === 'Asia/Qatar' ||
      timeZone === 'Asia/Kuwait' ||
      timeZone === 'Asia/Bahrain'
    ) {
      clues.timeZone = 'are-indicator';
    }
  } catch (_error) {
    // Browser doesn't support Intl.DateTimeFormat
  }

  return clues;
}

/**
 * Score and order data residency options based on available clues
 */
function getDataResidencyAttemptOrder(clues: LoginClues): SupportedDataResidency[] {
  const scores = {
    [SupportedDataResidency.USA]: 0,
    [SupportedDataResidency.ARE]: 0,
  };

  // Cached successful data residency gets highest priority (+8 points)
  if (clues.cachedDataResidency) {
    scores[clues.cachedDataResidency] += 8;
  }

  // Stored data residency preference gets high priority (+5 points)
  if (clues.storedDataResidency) {
    scores[clues.storedDataResidency] += 5;
  }

  // IP-based data residency gets medium priority (+3 points)
  scores[clues.ipDataResidency] += 3;

  // Email domain hints get +2 points
  if (clues.emailDomain === 'are-indicator') {
    scores[SupportedDataResidency.ARE] += 2;
  }

  // Browser language gets +1 point
  if (clues.browserLanguage === 'are-indicator') {
    scores[SupportedDataResidency.ARE] += 1;
  }

  // Timezone gets +1 point
  if (clues.timeZone === 'are-indicator') {
    scores[SupportedDataResidency.ARE] += 1;
  }

  // Return data residency options ordered by score (highest first)
  return Object.entries(scores)
    .sort(([, a], [, b]) => b - a)
    .map(([dataResidency]) => dataResidency as SupportedDataResidency);
}

/**
 * Intelligent multi-data-residency sign in.
 *
 * Tries every data residency once per round, in scored order. If a full round produces only
 * definitive failures (e.g. wrong password, user-not-found) it stops immediately and surfaces the
 * error. If a round saw a transient/retryable error (HTTP 500 / Cognito InternalError), it backs off
 * with exponential delay and cycles the whole set again, up to SIGN_IN_MAX_ROUNDS rounds, reporting
 * progress through options.onRetry.
 */
export async function intelligentDataResidencySignIn(
  username: string,
  password: string,
  options: IntelligentSignInOptions = {},
): Promise<AuthenticationResult> {
  const clues = await predictUserDataResidency(username);
  const dataResidencyOrder = getDataResidencyAttemptOrder(clues);
  const sleep = options.sleep ?? defaultSignInSleep;
  const getJitterMs = options.getJitterMs ?? getDefaultSignInJitterMs;

  console.log(`🎯 Login attempt order based on clues:`, dataResidencyOrder);
  console.log(`🔍 Clues used:`, clues);

  let lastError: Error | null = null;
  let attemptCount = 0;

  for (let round = 1; round <= SIGN_IN_MAX_ROUNDS; round++) {
    let sawRetryableError = false;

    for (const dataResidency of dataResidencyOrder) {
      attemptCount += 1;

      try {
        console.log(`🔐 Attempting sign in: ${dataResidency.toUpperCase()} (round ${round}/${SIGN_IN_MAX_ROUNDS})`);

        // Configure Amplify for this data residency (which determines AWS region)
        await initializeAmplifyForDataResidency(dataResidency);

        // Attempt authentication
        const user = await cognitoAuth.signIn(username, password);

        // SUCCESS: Cache the successful data residency and update app state
        DataResidencyCache.cacheSuccessfulLogin(username, dataResidency);
        dataResidencyManager.setDataResidency(dataResidency);
        refreshEndpoints(); // Update API endpoints to match data residency

        console.log(`✅ Sign in successful: ${dataResidency.toUpperCase()}`);

        return {
          user,
          dataResidency,
          attemptCount,
          cluesUsed: clues,
        };
      } catch (error) {
        lastError = error as Error;
        if (isRetryableCognitoError(error)) {
          sawRetryableError = true;
        }
        console.log(`❌ Sign in failed for ${dataResidency.toUpperCase()}:`, lastError.message);
      }
    }

    // A full round produced only definitive failures - no point cycling again.
    if (!sawRetryableError) {
      break;
    }

    // Transient failure seen this round - back off, then cycle the whole set again.
    if (round < SIGN_IN_MAX_ROUNDS) {
      options.onRetry?.({ attempt: round + 1, maxAttempts: SIGN_IN_MAX_ROUNDS });
      const delayMs = getSignInRoundDelayMs(round - 1, getJitterMs());
      console.warn(
        `[SignIn] Transient sign-in error(s) seen in round ${round}/${SIGN_IN_MAX_ROUNDS}; retrying after ${delayMs}ms`,
      );
      await sleep(delayMs);
    }
  }

  // All data residency options failed - throw the last error with additional context
  const enhancedError = new Error(
    `Authentication failed in all data residency options. Last error: ${lastError?.message || 'Unknown error'}`,
  );
  (enhancedError as any).originalError = lastError;
  (enhancedError as any).dataResidenciesAttempted = dataResidencyOrder;
  (enhancedError as any).cluesUsed = clues;

  throw enhancedError;
}

/**
 * Smart sign up that uses data residency prediction
 */
export async function intelligentDataResidencySignUp(
  username: string,
  password: string,
  attributes: any,
): Promise<AuthenticationResult> {
  const clues = await predictUserDataResidency(username);
  const dataResidencyOrder = getDataResidencyAttemptOrder(clues);

  // For sign up, we use the highest-scored data residency
  const targetDataResidency = dataResidencyOrder[0];

  console.log(`📝 Creating new account in ${targetDataResidency.toUpperCase()} data residency based on clues:`, clues);

  try {
    // Configure Amplify for target data residency (which determines AWS region)
    await initializeAmplifyForDataResidency(targetDataResidency);

    // Add data residency metadata to user attributes
    const enhancedAttributes = {
      ...attributes,
      'custom:dataResidency': targetDataResidency,
    };

    // Perform sign up
    const result = await cognitoAuth.signUp({
      username,
      password,
      attributes: enhancedAttributes,
    });

    // Cache the data residency for this user
    DataResidencyCache.cacheSuccessfulLogin(username, targetDataResidency);
    dataResidencyManager.setDataResidency(targetDataResidency);
    refreshEndpoints();

    console.log(`✅ Sign up successful in ${targetDataResidency.toUpperCase()} data residency`);

    return {
      user: result,
      dataResidency: targetDataResidency,
      attemptCount: 1,
      cluesUsed: clues,
    };
  } catch (error) {
    console.error(`❌ Sign up failed in ${targetDataResidency.toUpperCase()} data residency:`, error);
    throw error;
  }
}

/**
 * Utility function to get data residency prediction without authentication
 */
export async function predictOptimalDataResidency(email: string): Promise<{
  dataResidency: SupportedDataResidency;
  confidence: 'HIGH' | 'MEDIUM' | 'LOW';
  clues: LoginClues;
}> {
  const clues = await predictUserDataResidency(email);
  const dataResidencyOrder = getDataResidencyAttemptOrder(clues);
  const topDataResidency = dataResidencyOrder[0];

  // Calculate confidence based on clues
  let confidence: 'HIGH' | 'MEDIUM' | 'LOW' = 'LOW';

  if (clues.cachedDataResidency) {
    confidence = 'HIGH';
  } else if (clues.storedDataResidency || clues.emailDomain) {
    confidence = 'MEDIUM';
  } else {
    confidence = 'LOW';
  }

  return {
    dataResidency: topDataResidency,
    confidence,
    clues,
  };
}

// Export utility class
export { DataResidencyCache };

// Export types for other modules to use
export type { LoginClues, UserDataResidencyCache, AuthenticationResult };

// Legacy exports for backward compatibility
export { intelligentDataResidencySignIn as intelligentRegionSignIn };
export { intelligentDataResidencySignUp as intelligentRegionSignUp };
export { predictOptimalDataResidency as predictOptimalRegion };
export { DataResidencyCache as RegionCache };
export type { UserDataResidencyCache as UserRegionCache };
